import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Table,
  Tag,
  Button,
  Input,
  Select,
  Space,
  Drawer,
  Form,
  message,
  notification,
  Row,
  Col,
  Flex,
  InputNumber,
  Typography,
  Badge,
} from "antd";
import { SearchOutlined, FilterOutlined, InfoCircleFilled, UserOutlined, EditOutlined } from "@ant-design/icons";
import axiosInstance from "../axios";
import { useAtom } from "jotai";
import { userRole, userStudentCityAccess } from "../states/userRole";
import { citysList } from "../states/citys";
import PaymentCommentsPopup from "../components/student-drawer/PaymentCommentsPopupProps";
import DateFilterPicker from "../components/recurring-payment/DateFilterPicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { USER_ROLES } from "../constants/roleConstants";
import { STUDENT_DEALINGS_STATUS_COLORS } from "../components/constants";
dayjs.extend(utc);

const { Option } = Select;
const { Search } = Input;

const RecurringPayments = () => {
  const [dateFilter, setDateFilter] = useState({
    date_type: "due",
    from: "",
    to: "",
  });
  const [pendingFetch, setPendingFetch] = useState(false);
  const [pendingOverrides, setPendingOverrides] = useState(null);
  const [deals, setDeals] = useState([]);
  const [flatData, setFlatData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchField, setSearchField] = useState("Name");
  const [searchValue, setSearchValue] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [limit, setLimit] = useState(25);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [paymentsLimit, setPaymentsLimit] = useState(25);
  const paymentLimitOptions = useMemo(
    () => [
      { value: 25, label: "Range upto 25 latest payments" },
      { value: 100, label: "Range upto 100 latest payments" },
      { value: 500, label: "Range upto 500 latest payments" },
      { value: 1000, label: "Range upto 1000 latest payments" },
    ],
    [],
  );
  // atoms
  const [role] = useAtom(userRole);
  const [allCitys] = useAtom(citysList);
  const [citys] = useAtom(userStudentCityAccess);
  // console.log("citys",citys,"allCitys",allCitys)
  const [myTeamList, setMyTeamList] = useState([]);

  // Track initial mount so reactive effects don't fire on mount
  const isInitialMount = useRef(true);

  // Build params for requests consistently
  const buildParams = (overrides = {}) => {
    const base = {
      ...filters,
      ...overrides,
    };

    // include search if present
    if (searchValue) {
      base.search_field = searchField; // keep same contract you had earlier
      base.search_value = searchValue;
    }

    // pagination
    // base.page = overrides.page ?? pagination.current;
    // base.page_size = overrides.page_size ?? pagination.pageSize;
    base.page = 1;
    base.page_size = paymentsLimit;

    return base;
  };

  const fetchData = useCallback(
    async (overrides = {}) => {
      setLoading(true);
      try {
        const params = buildParams(overrides);
        if (dateFilter.date_type && dateFilter.from && dateFilter.to) {
          params.date_type = dateFilter.date_type === "due" ? "due_date" : dateFilter.date_type;
          params.from = dateFilter.from;
          params.to = dateFilter.to;
        }
        if (assignedToMe) {
          params.assigned_to_me = true;
        }
        const res = await axiosInstance.get("v1/recurring-payment-list", {
          params,
        });

        if (res.data?.is_success) {
          const rows = res.data.data?.rows || [];

          // Flatten deals → payments (skip deals with no payments)
          const flattened = rows.flatMap((deal) =>
            (deal.Allpayments || []).map((p) => ({
              // keep all deal fields (so existing column renderers work unchanged)
              ...deal,

              // attach payment fields we actually need in columns
              custom_amount: p.custom_amount,
              payment_id: p.id,
              payment_status: p.status_code, // used by "Payment Status" column
              payment_date: p.payment_date, // used by "Last Payment" column
              due_date: p.due_date, // used by "Due Date" column
              comment_count: p.comments_count ?? 0, // used by "Comments" column
              is_checked: p.is_checked,
              submit_banner_dismissed_at: p.submit_banner_dismissed_at,
              // optionally keep billing amount if you ever need it (not used by columns per your spec)
              billing_amount: p.billing_amount,
            })),
          );


          setDeals(rows);
          setFlatData(flattened);

          // For the table pagination UI, show number of payment rows in this page
          setPagination((p) => ({
            ...p,
            total: flattened.length,
          }));
        } else {
          message.error("Failed to fetch data");
        }
      } catch (err) {
        console.error("Error fetching data", err);
        message.error("Error fetching data");
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filters,
      searchField,
      searchValue,
      // pagination.current,
      pagination.pageSize,
      dateFilter,
      assignedToMe,
      paymentsLimit,
    ],
  );
  async function fetchMyTeamForFilter() {
    try {
      const { data } = await axiosInstance.get("/v1/cre/dealings/fetch-my-team");
      if (data && data.data.length) {
        // console.log("myteam", data.data);
        setMyTeamList(data.data);
      }
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    fetchData();
    fetchMyTeamForFilter();
    // Mark initial mount as done after first fetch
    isInitialMount.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dateFilter.from && dateFilter.to) {
      handleFilterSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);

  useEffect(() => {
    // Skip on initial mount — fetchData() in the mount effect already covers it
    if (isInitialMount.current) return;
    handleFilterSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedToMe]);

  useEffect(() => {
    // Skip on initial mount — fetchData() in the mount effect already covers it
    if (isInitialMount.current) return;
    handleFilterSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentsLimit]);

  const handleTableChange = (pg, _, sorter) => {
    setPagination((p) => ({
      ...p,
      current: pg.current,
      pageSize: pg.pageSize,
    }));
    const sortPayload = {};
    if (sorter && sorter.field) {
      sortPayload.sort_field = sorter.field;
      sortPayload.sort_order = sorter.order === "ascend" ? "asc" : "desc";
    }
    // fetchData({ page: pg.current, page_size: pg.pageSize, ...sortPayload });
  };

  useEffect(() => {
    if (!pendingFetch) return;

    const overrides = pendingOverrides || { page: 1 };

    fetchData(overrides)
      .catch((err) => {
        console.error("fetch after update failed", err);
      })
      .finally(() => {
        setPendingFetch(false);
        setPendingOverrides(null);
      });
    // fetchData intentionally omitted: we don't want this effect to re-run
    // just because fetchData's identity changed (it has many useCallback deps).
    // It only needs to run when pendingFetch/pendingOverrides change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFetch, pendingOverrides]);
  const handleFilterSubmit = () => {
    const body = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v !== undefined && v !== null && v !== ""),
    );

    // update state
    setFilters(body);
    setPagination((p) => ({ ...p, current: 1 }));
    setIsFilterOpen(false);

    // capture pageSize now (it won't change in this flow usually)
    const pageSize = pagination.pageSize;

    // build explicit overrides — include date filter / search if set
    const overrides = { page: 1, page_size: pageSize, ...body };
    if (dateFilter.date_type && dateFilter.from && dateFilter.to) {
      overrides.date_type = dateFilter.date_type === "due" ? "due_date" : dateFilter.date_type;
      overrides.from = dateFilter.from;
      overrides.to = dateFilter.to;
    }
    if (searchValue) {
      overrides.search_field = searchField;
      overrides.search_value = searchValue;
    }

    // trigger fetch after state updates
    setPendingOverrides(overrides);
    setPendingFetch(true);
  };
  const handleClearAll = () => {
    const resetDateFilter = { date_type: "due", from: "", to: "" };
    const resetFilters = {};
    const resetSearchField = "Name";
    const resetSearchValue = "";
    const pageSize = pagination.pageSize;

    // update UI state
    setDateFilter(resetDateFilter);
    setFilters(resetFilters);
    setSearchValue(resetSearchValue);
    setSearchField(resetSearchField);
    setPagination((p) => ({ ...p, current: 1 }));

    // Build overrides with only required pagination info
    const overrides = {
      page: 1,
      page_size: pageSize,
    };

    // 👉 notice we do NOT add from/to/date_type/search here at all

    // trigger fetch after state updates
    setPendingOverrides(overrides);
    setPendingFetch(true);
  };

  const STATUS_MAP = {
    ONGOING: { label: "Ongoing", color: "#22A50D" },
    PAUSED: { label: "Paused", color: "orange" },
    REPLACEMENT: { label: "Replacement", color: "blue" },
    DEAD: { label: "Dead", color: "red" },
  };

  const renderStatusTag = (status) => {
    if (!status) return "-";
    const m = STATUS_MAP[status] || { label: status, color: "#d9d9d9" };

    return (
      <Tag
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          borderRadius: "18px",
          backgroundColor: "white",
          border: `1.5px solid ${m.color}`,
          color: m.color,
          fontWeight: 600,
          padding: "2px 8px",
          margin: 0,
        }}
      >
        {m.label}
        <span
          style={{
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            backgroundColor: m.color,
          }}
        />
      </Tag>
    );
  };

  // 🔹 API call handler
  const handleStatusChange = async (dealId, newStatus) => {
    try {
      const res = await axiosInstance.put("v1/recurring/change-TutionStatus", {
        id: dealId,
        status: newStatus,
      });

      if (res.data?.is_success) {
        message.success("Status updated successfully!");
        fetchData();
      } else {
        message.error(res.data?.error || "Failed to update status");
      }
    } catch (err) {
      console.error("Error updating status:", err);
      message.error("Something went wrong while updating status");
    }
  };

  const renderPaymentStatus = (status) => {
    if (!status) return "-";
    let color = "default";
    let bgcolor = "default";
    if (status === "SUCCESS" || status === "RECEIVED") {
      bgcolor = "#CFFFB9";
      color = "#22A50D";
    }
    if (status === "CREATED" || status === "PAYMENT_PENDING") {
      bgcolor = "#FFF7CA";
      color = "#E0BC01";
    }
    if (status === "FAILURE") {
      color = "red";
    }
    return (
      <Tag
        style={{
          backgroundColor: bgcolor,
          color: color,
          fontWeight: 500,
          borderRadius: "18px",
          borderColor: bgcolor,
          padding: "2px 10px",
        }}
      >
        {status}
      </Tag>
    );
  };

  const renderDueDate = (date, paymentStatus) => {
    if (!date) return "-";

    // Parse the full date with timezone and convert to local timezone
    const d = dayjs(date).startOf("day");
    const today = dayjs().startOf("day");
    const daysDiff = d.diff(today, "day");


    if (paymentStatus === "SUCCESS") {
      return d.format("DD/MM/YYYY");
    } else {
      if (daysDiff === 0) {
        return <Tag color="red">Due Today</Tag>;
      } else if (daysDiff === 1) {
        return <Tag color="orange">Due Tomorrow</Tag>;
      } else if (daysDiff < 0) {
        return (
          <Tag color="red">
            Overdue {Math.abs(daysDiff)} day{Math.abs(daysDiff) > 1 ? "s" : ""}
          </Tag>
        );
      } else {
        return d.format("DD/MM/YYYY");
      }
    }
  };

  const renderNextDueDate = (date) => {
    console.log("date", date);
    if (!date) return "-";

    // Parse the full date with timezone and convert to local timezone
    const d = dayjs(date).startOf("day");
    const today = dayjs().startOf("day");
    const daysDiff = d.diff(today, "day");

    if (daysDiff > 3) {
      return d.format("DD/MM/YYYY");
    } else {
      if (daysDiff === 0) {
        return <Tag color="red">Next Due Today</Tag>;
      } else if (daysDiff === 1) {
        return <Tag color="orange">Next Due Tomorrow</Tag>;
      } else if (daysDiff === 2) {
        return <Tag color="orange">Next Due in 2 days</Tag>;
      } else if (daysDiff === 3) {
        return <Tag color="orange">Next Due in 3 days</Tag>;
      } else if (daysDiff < 0) {
        return (
          <Tag color="red">
            Overdue {Math.abs(daysDiff)} day{Math.abs(daysDiff) > 1 ? "s" : ""}
          </Tag>
        );
      }
    }
  };

  const paymentMonthFilters = Array.from(
    new Set(flatData.map((record) => record.Allpayments?.[0]?.payment_number).filter(Boolean)),
  ).map((month) => ({
    text:
      month === 1
        ? "1st Month payment"
        : month === 2
          ? "2nd Month payment"
          : month === 3
            ? "3rd Month payment"
            : `${month}th Month payment`,
    value: month,
  }));

  async function sendPaymentRecipt(paymentId) {
    try {
      axiosInstance
        .post(`/v1/cre/student/payment-reciept`, { paymentId })
        .then((response) => {
          const { is_success } = response.data;
          if (is_success) {
            notification.success({
              message: "Send Reciept",
              description: "Reciept sent to your email",
              duration: 2,
            });
          } else {
            notification.error({
              message: "Error",
              description: response.data?.error?.message || "Something went wrong",
              duration: 2,
            });
          }
        })
        .catch((error) => {
          const message =
            error?.response?.data?.error?.message || error?.response?.statusText || error?.message || "Unknown error";
          notification.error({
            message: "Error",
            description: message,
            duration: 2,
          });
        });
    } catch (error) {
      notification.error({
        message: "Error",
        description: error?.message || "Unexpected error",
        duration: 2,
      });
    }
  }

  async function emailCutomAmountPayment(values) {
    console.log("PAYMENT PAYLOAD >>>", values); // ✅ debug log
    try {
      axiosInstance
        .post(`/v1/cre/student/payment-order-invoice/${values.studentId}`, {
          orderId: values.orderId,
          dealId: values.dealId,
        })
        .then((response) => {
          const { is_success } = response.data;
          if (is_success) {
            notification.success({
              message: "Order Email",
              description: "Order Email Sent Successfully",
              duration: 2,
            });
          }
        })
        .catch((error) => {
          const errorMessage = error?.response?.data?.message || error?.message || "Something went wrong!";
          notification.error({
            message: "Error",
            description: errorMessage,
            duration: 2,
          });
        });
    } catch (error) {
      notification.error({
        message: "Error",
        description: error?.message || "Unexpected error",
        duration: 2,
      });
    }
  }
  const assignedToFilters = Array.from(new Set(flatData.map((item) => item.admin_first_name).filter(Boolean))).map(
    (name) => ({ text: name, value: name }),
  );

  const handleDownload = async () => {
    if (!limit) {
      notification.error({
        message: "Limit Required",
        description: "Please enter a limit value.",
      });
      return;
    }

    try {
      // Make API request to process bulk download
      const response = await axiosInstance.post("/v1/cre/download/", {
        limit,
        user_type: "Student Recurring Payment",
      });

      if (response.data && response.data?.resp_code === 200) {
        notification.success({
          message: "Requested Download Data",
          description: "Requested Download Data Successfully",
        });
        setLimit(0);
      } else {
        notification.error({
          message: "Failed to Process",
          description: response.data.resp_message || "Unable to process bulk download.",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred while processing bulk download.",
      });
    }
  };

  const columns = useMemo(() => {
    return [
      {
        title: "Deal ID",
        dataIndex: "id",
        key: "id",
        render: (val) => val || "-",
      },
      {
        title: "Customer Name",
        key: "customerName",
        render: (_, record) => (
          <a href={`/student/${record.id}`} target="_blank" rel="noopener noreferrer">
            {`${record.first_name || "-"} ${record.last_name || ""}`.trim()}
          </a>
        ),
      },
      {
        title: "Phone no.",
        dataIndex: "phone",
        key: "phone",
        render: (val) => val || "-",
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: "100px",
        render: (_, record) => {
          let status = record?.status;
          if (status === "pending" && record?.advance_payment) {
            status = "Processing";
          }
          return (
            <div style={{ minWidth: `${status?.length * 8}px` }}>
              <Tag color={STUDENT_DEALINGS_STATUS_COLORS[status]}>{status}</Tag>
            </div>
          );
        },
      },
      {
        title: "Status",
        dataIndex: "tution_status",
        key: "status",
        width: 170,
        align: "center",
        render: (cellValue, record) => (
          <Select
            value={cellValue}
            style={{ width: 160 }}
            onChange={(val) => handleStatusChange(record.id, val)}
            optionLabelProp="label" // ensures the label is shown
            suffixIcon={<EditOutlined style={{ color: "#555" }} />}
            bordered={false}
            dropdownRender={(menu) => <div style={{ padding: 4 }}>{menu}</div>}
          >
            {/* 🔹 Current status as selected option */}
            <Select.Option key={cellValue} value={cellValue} label={renderStatusTag(cellValue)}>
              {renderStatusTag(cellValue)}
            </Select.Option>

            {/* 🔹 Other statuses */}
            {Object.keys(STATUS_MAP)
              .filter((key) => key !== cellValue)
              .map((key) => (
                <Select.Option key={key} value={key} label={renderStatusTag(key)}>
                  {renderStatusTag(key)}
                </Select.Option>
              ))}
          </Select>
        ),
      },
      {
        title: "Start Date",
        key: "start_date",
        align: "center",
        width: 100,
        sorter: (a, b) => {
          const aDate = a.shortlist?.find((s) => s.status === "CONFIRMED" && s.start_date)?.start_date;
          const bDate = b.shortlist?.find((s) => s.status === "CONFIRMED" && s.start_date)?.start_date;
          return dayjs(aDate).unix() - dayjs(bDate).unix();
        },
        render: (_, record) => {
          const confirmedShortlist = record.shortlist?.find((s) => s.status === "CONFIRMED" && s.start_date);
          return confirmedShortlist?.start_date ? dayjs(confirmedShortlist.start_date).format("DD/MM/YYYY") : "-";
        },
      },
      {
        title: "Tutor Details",
        key: "tutor_details",
        width: 120,
        align: "center",
        render: (_, record) => {
          const tutor =
            record.shortlist?.find((s) => s.status === "CONFIRMED") ||
            record.shortlist?.find((s) => s.status === "ASSIGNED");
          return tutor?.tutorID ? (
            <Button
              type="link"
              size="small"
              style={{ paddingLeft: "20px" }}
              onClick={() => {
                const url = `/tutor/${tutor.tutorID}`;
                window.open(url, "_blank");
              }}
              aria-label={`Open details for Tutor ID: ${tutor.tutorID}`}
            >
              <span style={{ color: "black" }}>Details</span>
              <InfoCircleFilled />
            </Button>
          ) : (
            "-"
          );
        },
      },
      {
        title: "Monthly Fees",
        key: "fee_discuss",
        align: "center",
        width: 120,
        render: (_, record) => {
          return record.fee_discuss !== null && record.fee_discuss !== undefined
            ? `${record.fee_discuss.toString()} /-`
            : "-";
        },
      },
      {
        title: "Payment Month",
        key: "payment_number",
        align: "center",
        width: 150,
        filters: paymentMonthFilters, // ✅ dynamically generated
        onFilter: (value, record) => record.Allpayments?.[0]?.payment_number === value,
        render: (_, record) => {
          const month = record.Allpayments?.[0]?.payment_number;
          if (!month) return "-";

          if (month === 1) return "1st Month payment";
          if (month === 2) return "2nd Month payment";
          if (month === 3) return "3rd Month payment";
          return `${month}th Month payment`;
        },
        onHeaderCell: () => ({
          style: { paddingRight: "24px" },
        }),
      },
      {
        title: "Amount Received",
        key: "amount_received",
        align: "center",
        width: 150,
        render: (record) =>
          record.payment_status === "SUCCESS" && record.custom_amount !== null ? `₹${record.custom_amount} /-` : "-",
      },
      {
        title: "Payment Status",
        dataIndex: "payment_status", // flattened field
        align: "center",
        key: "payment_status",
        width: 130,
        render: (val) => renderPaymentStatus(val),
      },
      {
        title: "Tutor Payment",
        key: "tutor_payment",
        align: "center",
        width: 120,
        render: (record) => {
          // if (record.fee_discuss && record.fee_percent) {
          //   const companyPct = getCompanyPercent(record.fee_percent);
          //   if (companyPct !== null) {
          //     const tutorAmount = (Number(record.fee_discuss) * (100 - companyPct)) / 100;
          //     return `₹${tutorAmount.toFixed(2)}`;
          //   }
          // }
          return "-";
        },
      },
      {
        title: "Due Date",
        dataIndex: "due_date",
        align: "center",
        width: 120,
        key: "due_date",
        sorter: (a, b) => {
          if (!a.due_date && !b.due_date) return 0;
          if (!a.due_date) return -1;
          if (!b.due_date) return 1;
          return dayjs(a.due_date).unix() - dayjs(b.due_date).unix();
        },
        render: (val, record) => renderDueDate(val, record.payment_status),
      },
      {
        title: "Last Payment Date",
        dataIndex: "payment_date",
        align: "center",
        key: "payment_date",
        width: 180,
        sorter: (a, b) => {
          if (!a.payment_date && !b.payment_date) return 0;
          if (!a.payment_date) return -1;
          if (!b.payment_date) return 1;
          return dayjs(a.payment_date).unix() - dayjs(b.payment_date).unix();
        },
        render: (val) => (val ? dayjs(val).format("DD/MM/YYYY") : "-"),
      },
      {
        title: "Tutor Marked Attendance",
        dataIndex: "is_checked",
        align: "center",
        key: "is_checked",
        width: 180,
        render: (val) => (val ? val ? 'Yes' : "No" : "-"),
      },
      {
        title: "Tutor Marked Attendance Date",
        dataIndex: "submit_banner_dismissed_at",
        align: "center",
        key: "submit_banner_dismissed_at",
        width: 180,
        render: (val) => (val ? dayjs(val).format("DD/MM/YYYY") : "-"),
      },
      {
        title: "Next Due Date",
        key: "next_due_date",
        align: "center",
        width: 180,
        sorter: (a, b) => {
          const aDate = a.Allpayments?.[0]?.next_due_date;
          const bDate = b.Allpayments?.[0]?.next_due_date;
          if (!aDate && !bDate) return 0;
          if (!aDate) return -1;
          if (!bDate) return 1;
          return dayjs(aDate).unix() - dayjs(bDate).unix();
        },
        render: (_, record) => {
          const val = record.Allpayments?.[0]?.next_due_date;
          return val ? renderNextDueDate(val) : "-";
        },
      },
      {
        title: "Actions",
        width: 175,
        align: "center",
        render: (_, order, index) => (
          <Button
            type="primary"
            style={{
              backgroundColor: order.payment_status === "SUCCESS" ? "#58b378ff" : "#007bff",
              borderColor: order.payment_status === "SUCCESS" ? "#58b378ff" : "#007bff",
              // color: order.payment_status === "SUCCESS" ? "#58b378ff" : "#fff",
            }}
            onClick={() => {
              console.log(order);
              if (order.payment_status === "SUCCESS") {
                sendPaymentRecipt(order.Allpayments[0].payment_id);
              } else {
                emailCutomAmountPayment({
                  orderId: order.Allpayments[0].order_id,
                  dealId: order.id,
                  studentId: order.studentId,
                });
              }
            }}
          >
            {order.payment_status === "SUCCESS" ? "Send Receipt" : "Send Payment Link"}
          </Button>
        ),
      },
      {
        title: "Policy",
        dataIndex: "fee_percent",
        key: "fee_percent",
        render: (val) => val || "-",
      },
      {
        title: "Tuition type",
        dataIndex: "tution_type",
        key: "tution_type",
        render: (val) => (val ? String(val).charAt(0).toUpperCase() + String(val).slice(1) : "-"),
      },
      {
        title: "City",
        dataIndex: "city_id",
        key: "city_id",
        render: (val) => (val ? (citys && allCitys[val] ? allCitys[val] : `#${val}`) : "-"),
      },
      {
        title: "Assigned To",
        key: "assigned_to",
        dataIndex: "admin_first_name",
        // width: 180,
        marginInline: "10px",
        filters: assignedToFilters,
        onFilter: (value, record) => record.admin_first_name === value,
        render: (_, record) => {
          const fullName = [record.admin_first_name, record.admin_last_name].filter(Boolean).join(" ");
          return fullName || "-";
        },
        onHeaderCell: () => ({
          style: { paddingRight: "24px" },
        }),
      },
      {
        title: "Comments",
        key: "comments",
        align: "center",
        render: (_, record) => (
          <PaymentCommentsPopup
            cellValue={record.comment_count}
            data={{
              payment_id: record.payment_id,
              comment_count: record.comment_count,
            }}
          />
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citys, filters, flatData]);

  // validation for search
  const validateSearch = (field, value) => {
    if (field === "Name") {
      return /^[A-Za-z\s]*$/.test(value); // only alphabets & spaces
    }
    if (field === "Phone") {
      return /^\d{0,10}$/.test(value); // only numbers, up to 10 digits
    }
    if (field === "DealID") {
      return /^\d*$/.test(value); // only numbers
    }
    return true;
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (validateSearch(searchField, value)) {
      setSearchValue(value);
    }
  };

  const onSearch = (value) => {
    if (!validateSearch(searchField, value)) {
      message.error("Invalid input for " + searchField);
      return;
    }
    setSearchValue(value);
    setPagination((p) => ({ ...p, current: 1 }));
    fetchData({ page: 1 });
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;

    // Deal-level filters
    if (filters.tution_status) count += 1;
    if (filters.latest_payment_status) count += 1;
    if (filters.tution_type) count += 1;
    if (filters.city_id) count += 1;
    if (filters.assigned_to) count += 1;

    // Date filter
    if (dateFilter?.from || dateFilter?.to) count += 1;

    // Search
    if (searchValue) count += 1;

    return count;
  }, [filters, dateFilter, searchValue]);

  return (
    <div style={{ padding: 20, background: "#fff" }}>
      {/* Filters & Search Row */}
      <div
        style={{
          display: "flex",
          gap: "18px",
          marginBottom: "12px",
          marginTop: "12px",
          alignItems: "center",
        }}
      >
        {/* 1st: Field */}
        <DateFilterPicker dateFilter={dateFilter} setDateFilter={setDateFilter} />

        {/* <pre>{JSON.stringify(dateFilter, null, 2)}</pre> */}
        <Space
          style={{
            display: "flex",
            gap: "18px",
            marginBottom: "0px",
            alignItems: "center",
          }}
          wrap
        >
          <Select value={searchField} onChange={setSearchField} style={{ width: 140 }}>
            <Option value="Name">Name</Option>
            <Option value="Phone">Phone</Option>
            <Option value="DealID">Deal ID</Option>
          </Select>
          <Search
            placeholder="Search"
            value={searchValue}
            onChange={handleSearchChange}
            onSearch={onSearch}
            enterButton
            prefix={<SearchOutlined />}
            style={{ width: 320 }}
            allowClear
          />

          {/* Funnel Icon */}
          <Badge count={activeFilterCount} offset={[2, 0]}>
            <Button icon={<FilterOutlined />} onClick={() => setIsFilterOpen(true)} aria-label="Open filters" />
          </Badge>
          <Button onClick={handleClearAll}>Clear Filters</Button>
        </Space>
      </div>
      {role === USER_ROLES.SUPERADMIN && (
        <Flex justify="flex-end">
          <Row>
            <Col>
              <InputNumber
                min={1}
                max={24}
                placeholder="Enter Limit"
                value={limit}
                onChange={(value) => setLimit(value)} // No need to access `e.value`, just use `value`
                style={{ margin: "10px", width: "150px" }}
              />
              <Typography.Text>Limit (in Months)</Typography.Text>
            </Col>
            <Col>
              <Button
                type="primary"
                style={{
                  margin: "10px",
                  color: "white",
                  backgroundColor: "rgba(39, 65, 120, 1)",
                }}
                onClick={handleDownload}
              >
                Download
              </Button>
            </Col>
          </Row>
        </Flex>
      )}
      {role !== USER_ROLES.SUPERADMIN && (
        <Button
          icon={<UserOutlined />}
          color="primary"
          variant={assignedToMe ? "solid" : "outlined"}
          onClick={() => {
            setAssignedToMe(!assignedToMe);
            console.log("assignedToMe", assignedToMe);
          }}
          style={{ float: "right" }}
        >
          Assigned to me
        </Button>
      )}

      {/* Main Table */}
      <Table
        rowKey="payment_id" // 👈 one row per payment
        columns={columns}
        dataSource={flatData}
        loading={loading}
        pagination={{
          current: pagination.current,
          // pageSize: pagination.pageSize,
          pageSize: 10,
          total: pagination.total,
          showSizeChanger: false,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
        style={{ marginTop: "20px", paddingTop: "20px" }}
      />
      <Space style={{ marginBottom: 16, float: "right" }}>
        <Select
          value={paymentsLimit}
          onChange={(val) => {
            setPaymentsLimit(val);
            handleFilterSubmit();
          }}
          style={{ width: 250 }}
        >
          {paymentLimitOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
      </Space>

      {/* Filter Drawer */}
      <Drawer title="Filter" placement="right" onClose={() => setIsFilterOpen(false)} open={isFilterOpen}>
        <Form layout="vertical">
          {/* This still sends deal-level latest status filter to backend (if supported) */}
          <Form.Item label="status">
            <Select
              allowClear
              onChange={(val) => setFilters((prev) => ({ ...prev, tution_status: val }))}
              value={filters.tution_status}
            >
              <Option value="ONGOING">Ongoing</Option>
              <Option value="PAUSED">Paused</Option>
              <Option value="DEAD">Dead</Option>
              <Option value="REPLACEMENT">Replacement</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Payment Status">
            <Select
              allowClear
              onChange={(val) => setFilters((prev) => ({ ...prev, latest_payment_status: val }))}
              value={filters.latest_payment_status}
            >
              <Option value="CREATED">Created</Option>
              <Option value="SUCCESS">Success</Option>
              <Option value="FAILURE">Failure</Option>
              <Option value="PROCESSING">Processing</Option>
              <Option value="REJECTED">Rejected</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Tuition Type">
            <Select
              allowClear
              onChange={(val) => setFilters((prev) => ({ ...prev, tution_type: val }))}
              value={filters.tution_type}
            >
              <Option value="online">Online</Option>
              <Option value="offline">Offline</Option>
            </Select>
          </Form.Item>
          <Form.Item label="City">
            <Select
              allowClear
              onChange={(val) =>
                setFilters((prev) => ({
                  ...prev,
                  city_id: role === USER_ROLES.SUPERADMIN ? val : citys[val],
                }))
              }
              value={role === USER_ROLES.SUPERADMIN ? filters.city_id : allCitys[filters.city_id]}
            >
              {role === USER_ROLES.SUPERADMIN
                ? Object.entries(allCitys).map(([id, name]) => (
                  <Option key={id} value={id}>
                    {name}
                  </Option>
                ))
                : citys.map((cityId, index) => (
                  <Option key={index} value={index}>
                    {allCitys[cityId]}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          {role === USER_ROLES.ADMIN && (
            <Form.Item label="Assigned To">
              <Select
                allowClear
                onChange={(val) => {
                  setAssignedToMe(false);
                  setFilters((prev) => ({ ...prev, assigned_to: val }));
                }}
                value={filters.assigned_to}
              >
                {myTeamList &&
                  myTeamList.map((team) => (
                    <Option key={team.admin_id} value={team.admin_id}>
                      {team.admin_name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          )}

          <div style={{ display: "flex", gap: 8 }}>
            <Button type="primary" onClick={handleFilterSubmit}>
              Submit
            </Button>
            <Button onClick={() => setIsFilterOpen(false)}>Cancel</Button>
          </div>
        </Form>
      </Drawer>
    </div>
  );
};

export default RecurringPayments;
